import { useState, useMemo } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Input } from "@progress/kendo-react-inputs";
import {
    Grid,
    GridColumn,
    GridCellProps,
    GridSortChangeEvent,
} from "@progress/kendo-react-grid";
import { orderBy, SortDescriptor } from "@progress/kendo-data-query";
import { AmsInsuranceAgent } from "../../../Interfaces/Insurance/AmsInsuranceAgent";

interface InsuranceAgentSelectorProps {
    visible: boolean;
    agents: AmsInsuranceAgent[];
    onSelect: (agent: AmsInsuranceAgent) => void;
    onCancel: () => void;
}

export function InsuranceAgentSelector({
    visible,
    agents,
    onSelect,
    onCancel,
}: InsuranceAgentSelectorProps) {
    const [searchText, setSearchText] = useState("");
    const [selectedAgent, setSelectedAgent] = useState<AmsInsuranceAgent | null>(null);
    const [sort, setSort] = useState<SortDescriptor[]>([
        { field: "compName", dir: "asc" },
        { field: "agentName", dir: "asc" },
    ]);

    const filteredAndSortedAgents = useMemo(() => {
        let filtered = agents;

        if (searchText.trim()) {
            const lowerSearch = searchText.toLowerCase();
            filtered = agents.filter((agent) => {
                return (
                    agent.compName?.toLowerCase().includes(lowerSearch) ||
                    agent.agentName?.toLowerCase().includes(lowerSearch) ||
                    agent.compPhone?.toLowerCase().includes(lowerSearch) ||
                    agent.agentPhone?.toLowerCase().includes(lowerSearch) ||
                    agent.compAddr1?.toLowerCase().includes(lowerSearch) ||
                    agent.compAddr2?.toLowerCase().includes(lowerSearch) ||
                    agent.agentAddr1?.toLowerCase().includes(lowerSearch) ||
                    agent.agentAddr2?.toLowerCase().includes(lowerSearch) ||
                    agent.compCity?.toLowerCase().includes(lowerSearch) ||
                    agent.agentCity?.toLowerCase().includes(lowerSearch)
                );
            });
        }

        const sorted = orderBy(filtered, sort);

        return sorted.map((agent) => ({
            ...agent,
            selected: selectedAgent?.agentCode === agent.agentCode,
        }));
    }, [agents, searchText, sort, selectedAgent]);

    const handleSortChange = (event: GridSortChangeEvent) => {
        setSort(event.sort);
    };

    const handleRowClick = (event: any) => {
        setSelectedAgent(event.dataItem);
    };

    const handleSelect = () => {
        if (selectedAgent) {
            onSelect(selectedAgent);
            setSearchText("");
            setSelectedAgent(null);
        }
    };

    const handleCancel = () => {
        setSearchText("");
        setSelectedAgent(null);
        onCancel();
    };

    const CustomCell = (props: GridCellProps) => {
        return (
            <td style={{ cursor: "pointer" }}>
                {props.field && props.dataItem[props.field]}
            </td>
        );
    };

    if (!visible) return null;

    return (
        <Dialog
            title="Select Insurance Company / Agent"
            onClose={handleCancel}
            width={1250}
            height={700}
        >
            <div style={{ marginBottom: "1rem" }}>
                <Input
                    placeholder="Search by company, agent, phone, or address..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.value || "")}
                    style={{ width: "100%" }}
                />
            </div>

            <div style={{ height: "500px" }}>
                <Grid
                    data={filteredAndSortedAgents}
                    sortable={true}
                    sort={sort}
                    onSortChange={handleSortChange}
                    onRowClick={handleRowClick}
                    selectedField="selected"
                    style={{ height: "100%" }}
                >
                    <GridColumn field="compName" title="Insurance Company" width="250px" cell={CustomCell} />
                    <GridColumn field="agentName" title="Agent Name" width="200px" cell={CustomCell} />
                    <GridColumn field="compPhone" title="Company Phone" width="150px" cell={CustomCell} />
                    <GridColumn field="agentPhone" title="Agent Phone" width="150px" cell={CustomCell} />
                    <GridColumn field="compAddr1" title="Company Address" width="200px" cell={CustomCell} />
                    <GridColumn field="compCity" title="City" width="120px" cell={CustomCell} />
                    <GridColumn field="compState" title="State" width="80px" cell={CustomCell} />
                </Grid>
            </div>

            <DialogActionsBar>
                <Button fillMode="outline" themeColor="base" onClick={handleCancel}>
                    Cancel
                </Button>
                <Button
                    fillMode="solid"
                    themeColor="primary"
                    onClick={handleSelect}
                    disabled={!selectedAgent}
                >
                    Select
                </Button>
            </DialogActionsBar>
        </Dialog>
    );
}
